export type RoleKey = string;

export type RoleContext = "owner" | "employee" | "external";

// Q4 — Revenue / maturity stage (unscrapeable; ED status signal). "" = unanswered.
// Early end is granular: idea → mvp → pre_revenue before any revenue exists.
export type RevenueStage =
  | "idea"
  | "mvp"
  | "pre_revenue"
  | "early_revenue"
  | "growth"
  | "scale"
  | "";

// Q5 — Priorities Raisy should focus on first (multi-select; bot-vs-human signal, MR5).
export type FirstTask =
  | "raise_money"
  | "lead_generation"
  | "admin"
  | "follow_ups"
  | "reporting"
  | "scheduling"
  | "research"
  | "other";

export interface UseCase {
  key: string;
  label: string;
  description: string;
}

export interface ExtractedPrimaryEntity {
  suggested_name: string;
  entity_type: string;
  entity_type_sub: string | null;
  entity_offering_type: string | null;
  entity_offering_delivery: string | null;
  description: string | null;
  industry: string | null;
  target_market: string | null;
  brand_tone: string | null;
  confidence: number;
  confidence_dots: number;
  low_confidence: boolean;
}

export interface ExtractedSubEntity {
  suggested_name: string;
  entity_type: string;
  entity_type_sub: string | null;
  entity_offering_type: string | null;
  entity_offering_delivery: string | null;
  description: string | null;
  confidence: number;
  confidence_dots: number;
  is_part_of_parent: boolean;
  /** parent_child/employee/governance_role (own) or client/supplier/partner/investor
   * (external) — server-computed default (see raisy_78 entities_detected in the BE), the
   * discovery review step lets the user exclude candidates but not relabel this today. */
  relationship_type: string;
  role_title: string | null;
  /** Discovery review (backlog #18) — defaults to true (accept-all); toggled via the
   * accept/reject buttons on the Review step. Only accepted candidates are sent to
   * POST /entity/{id}/extraction/confirm. */
  accepted: boolean;
}

export interface ExtractedReview {
  primary_entity: ExtractedPrimaryEntity;
  sub_entities: ExtractedSubEntity[];
  has_duplicate_warning: boolean;
  save_warning?: string | null;
  /** Quick setup — the scan read the entity and did not search for other products, brands or
   * people, so the empty sub_entities list means "not looked for", never "none found". The
   * Review step says so, and names where the full scan is available. */
  quick_setup?: boolean;
}

export interface UrlValidationResult {
  status: "new" | "in_progress_session" | "existing_entity" | "unreachable";
  normalized_url?: string;
  session_id?: string;
  current_step?: number;
  entity_id?: string;
  entity_name?: string;
  entity_type?: string;
  similarity?: number;
}

export interface EntityDetails {
  name: string;
  entity_type: string;
  description: string;
  industry: string;
  target_market: string;
  brand_tone: string;
  parent_entity_ids: string[];
}

export interface RelatedEntity {
  name: string;
  entity_type: string;
  status: "extracting" | "added" | "error";
}

export type BuildScope = "light" | "standard" | "deep" | "";

export interface DetectedPerson {
  entity_id: string;
  entity_name: string;
}

export interface SelfMatchDecision {
  isMe: boolean;
  entityId: string | null;
}

export interface DuplicateDecision {
  status: "idle" | "pending" | "confirmed" | "creating";
  existingEntityId: string;
  existingEntityName: string;
  existingEntityType: string;
  similarity: number;
  /** Body to resend if the user chooses to force-create anyway. */
  createBody: Record<string, unknown> | null;
  /** Which path triggered this 409 — lets handleDuplicateDecision know whether to
   * rejoin the URL-extraction flow or the manual-search flow (PROJECT-077). */
  origin?: "url" | "manual";
  /** Manual path only — the candidate's resolved URL, if any, to hand off to
   * openExtractionSocket once the duplicate decision is made. */
  pendingUrl?: string | null;
}

// ── PROJECT-077: manual-entry AI web-search resolver ──────────────────────────

// T0117 — a possible existing-entity match for a search candidate. There can be more
// than one (e.g. two different "David" entities already in Raisy) — the merge-review
// popup lets the user pick which, if any, is really the same as what was just found.
export interface ExistingEntityMatch {
  entity_id: string;
  entity_name: string;
  entity_type: string;
  /** Comparison context so two same-named entities (e.g. two "Chaithanya"s in different
   * roles) are actually distinguishable in the merge-review picker, not just a bare name. */
  entity_description: string | null;
  person_role: string | null;
  entity_location: string | null;
  score: number;
}

export interface ManualSearchCandidate {
  entity_name: string;
  entity_type: string;
  entity_type_sub: string | null;
  entity_offering_type: string | null;
  entity_offering_delivery: string | null;
  /** The other side of a person/company pairing — a person's company/org, or a company's
   * notable associated person + role. Null if not evident from search results. */
  affiliation: string | null;
  /** Person candidates only — job title at the affiliated company. Kept separate from
   * affiliation since two same-named people can share a company but differ by role. */
  role: string | null;
  /** City/country, only when evident from search results — never guessed. */
  location: string | null;
  url: string | null;
  entity_links: Record<string, string> | null;
  confidence_score: number;
  evidence: string | null;
  /** Convenience mirror of existing_matches[0] — kept for callers that only care about
   * "is there any match at all," not which one. */
  already_in_raisy: boolean;
  existing_entity_id: string | null;
  existing_entity_name: string | null;
  /** T0117 — full candidate list (may be empty). Drives the merge-review popup. */
  existing_matches: ExistingEntityMatch[];
  /** True when the duplicate check itself couldn't run (embedding backend down —
   * quota/network), not that it ran and found nothing. existing_matches is unreliable
   * when this is true: it will be empty even if a real duplicate exists. */
  dedup_check_skipped: boolean;
}

export interface ManualSearchClarification {
  question: string;
  answer: string;
}

export interface WizardState {
  step: number;
  direction: 1 | -1;
  // Step 2 (URL extract)
  url: string;
  urlValidation: UrlValidationResult | null;
  isValidatingUrl: boolean;
  extractionPct: number;
  extractionMessage: string;
  isExtracting: boolean;
  extractedReview: ExtractedReview | null;
  detectedPersonEntities: DetectedPerson[];
  selfMatchDecision: SelfMatchDecision | null;
  duplicateDecision: DuplicateDecision | null;
  // Discovery review (backlog #18) — POST /entity/{id}/extraction/confirm status for the
  // currently accepted extractedReview.sub_entities. Guards against re-confirming (and
  // double-creating) candidates if the user navigates Back to the Review step afterward.
  candidatesConfirmed: boolean;
  isConfirmingCandidates: boolean;
  confirmCandidatesError: string | null;
  // Step 3 (role)
  selectedRoles: RoleKey[];
  roleOther: string;
  // Step 4 (goals)
  useCaseOptions: UseCase[];
  selectedUseCases: string[];
  // Q4 — Revenue stage (NEW). The raise profile (round / amount / ticket) is NOT
  // captured here — it belongs in the fundraising process (investor outreach), not
  // entity setup.
  revenueStage: RevenueStage;
  // Q5 — Priorities to focus on first (multi-select, NEW). `firstTaskOther` holds the
  // free-text when "other" is among firstTasks.
  firstTasks: FirstTask[];
  firstTaskOther: string;
  // Step 2 — visibility and entity type chosen before extraction
  visibility: "public" | "private";
  entityTypeHint: "company" | "product" | "service" | "person";
  /** Quick setup (wizard step 1) — ask the scraper for the entity alone instead of the full
   * discovery run. Sent as `quick_scrape` on the extraction socket. Meaningful for the FIRST
   * scan only: the entity's own fields are identical either way, so there is nothing to
   * re-decide later, and the sub-entities a full run would have offered appear in the review
   * only if this was off. */
  quickScrape: boolean;
  // Step 1 — own vs. external entity choice (revisitable via Back from Step 2). Client-side
  // flow control only: it decides which Step 3 role options to show and whether steps 4-7
  // are skipped, but is never sent to the backend as its own field. What actually persists
  // own-vs-external is whichever relationship_type Step 3 resolves to (see
  // roleConfig.EXTERNAL_ROLE_OPTIONS).
  isExternal: boolean;
  // Step 4 (canvas) — entity_id is the session key
  entityId: string | null;
  // Submit
  isSubmitting: boolean;
  processId: string | null;
  error: string | null;
  buildScope: BuildScope;
  // Deprecated: kept for backward compat with old step files pending Phase 4 cleanup
  entityDetails?: EntityDetails | null;
  relatedEntities?: RelatedEntity[];
  // PROJECT-077 — manual-entry AI web-search resolver (Step1Extract's "No website?
  // Describe it manually" panel). searching→resolved (candidates to pick from) or
  // ambiguous (a clarifying question is pending); error is a search-call failure.
  manualSearchStatus: "idle" | "searching" | "resolved" | "ambiguous" | "error";
  manualSearchCandidates: ManualSearchCandidate[];
  manualSearchQuestions: string[];
  manualSearchSelectedIndex: number | null;
  manualSearchClarifications: ManualSearchClarification[];
  manualSearchError: string | null;
  /** "Use my description as-is" fallback. Lets the manual path always complete the
   * wizard even when search finds nothing (fixes the prior dead end). */
  manualEscapeHatch: boolean;
}

export type WizardAction =
  | { type: "SET_STEP"; step: number; direction: 1 | -1 }
  | { type: "SET_URL"; url: string }
  | { type: "SET_URL_VALIDATION"; result: UrlValidationResult | null }
  | { type: "SET_VALIDATING_URL"; value: boolean }
  | { type: "SET_EXTRACTION_PROGRESS"; pct: number; message: string }
  | { type: "SET_EXTRACTING"; value: boolean }
  | { type: "SET_EXTRACTED_REVIEW"; review: ExtractedReview }
  | { type: "ADD_DETECTED_PERSON"; person: DetectedPerson }
  | { type: "SET_SELF_MATCH"; decision: SelfMatchDecision }
  | { type: "SET_DUPLICATE_DECISION"; decision: DuplicateDecision | null }
  | { type: "TOGGLE_CANDIDATE"; index: number }
  | { type: "SET_CONFIRMING_CANDIDATES"; value: boolean }
  | { type: "SET_CANDIDATES_CONFIRMED"; value: boolean }
  | { type: "SET_CONFIRM_CANDIDATES_ERROR"; error: string | null }
  | { type: "SET_ROLE"; role: RoleKey }
  | { type: "SET_ROLE_OTHER"; value: string }
  | { type: "SET_USE_CASE_OPTIONS"; options: UseCase[] }
  | { type: "TOGGLE_USE_CASE"; key: string }
  | { type: "SET_ENTITY_DETAILS"; details: EntityDetails }
  | { type: "PATCH_ENTITY_DETAILS"; patch: Partial<EntityDetails> }
  | { type: "INIT_RELATED_ENTITIES"; entities: RelatedEntity[] }
  | { type: "ADD_RELATED_ENTITY"; entity: RelatedEntity }
  | { type: "UPDATE_RELATED_ENTITY"; index: number; patch: Partial<RelatedEntity> }
  | { type: "SET_SUBMITTING"; value: boolean }
  | { type: "SET_PROCESS_ID"; processId: string }
  | { type: "SET_ENTITY_ID"; entityId: string }
  | { type: "SET_ERROR"; error: string | null }
  | { type: "SET_VISIBILITY"; value: "public" | "private" }
  | { type: "SET_ENTITY_TYPE_HINT"; value: "company" | "product" | "service" | "person" }
  | { type: "SET_QUICK_SCRAPE"; value: boolean }
  | { type: "SET_IS_EXTERNAL"; value: boolean }
  | { type: "SET_BUILD_SCOPE"; value: BuildScope }
  | { type: "SET_REVENUE_STAGE"; value: RevenueStage }
  | { type: "TOGGLE_FIRST_TASK"; value: FirstTask }
  | { type: "SET_FIRST_TASK_OTHER"; value: string }
  | { type: "SET_MANUAL_SEARCH_STATUS"; value: WizardState["manualSearchStatus"] }
  | { type: "SET_MANUAL_SEARCH_RESULT"; candidates: ManualSearchCandidate[]; questions: string[] }
  | { type: "SELECT_MANUAL_CANDIDATE"; index: number | null }
  | { type: "ADD_MANUAL_CLARIFICATION"; question: string; answer: string }
  | { type: "SET_MANUAL_SEARCH_ERROR"; error: string | null }
  | { type: "SET_MANUAL_ESCAPE_HATCH"; value: boolean }
  | { type: "RESET_MANUAL_SEARCH" }
  | { type: "RESET" };

export const ENTITY_TYPE_LABELS: Record<string, string> = {
  // current top-level types
  company: "Company",
  person: "Person",
  // legacy aliases (migration 027 and earlier)
  product: "Product",
  service: "Service",
  platform: "Platform",
  organisation: "Organisation",
  governance_body: "Governing Body",
  function: "Internal Function",
};

export const ENTITY_TYPE_SUB_LABELS: Record<string, string> = {
  Commercial: "Commercial",
  NGO: "NGO",
  Government: "Government",
  Consortium: "Consortium",
  Partnership: "Partnership",
  Financial_Vehicle: "Financial Vehicle",
  Educational: "Educational",
  Sole_Proprietor: "Sole Proprietor",
  Freelancer: "Freelancer",
  Independent_Creator: "Independent Creator",
  Employee: "Employee",
  Investor: "Investor",
};

export const ENTITY_OFFERING_TYPE_LABELS: Record<string, string> = {
  Product: "Product",
  Service: "Service",
  Asset_IP: "Asset / IP",
};

export const ENTITY_OFFERING_DELIVERY_LABELS: Record<string, string> = {
  SaaS_Cloud: "SaaS / Cloud",
  Digital_File: "Digital File",
  Physical_Good: "Physical Good",
  Operational_Labor: "Operational Labour",
  API_Access: "API Access",
  License_IP_Right: "License / IP Right",
  Commodity: "Commodity",
  Financial_Instrument: "Financial Instrument",
  Hybrid_IoT: "Hybrid IoT",
  Utility_Infrastructure: "Utility / Infrastructure",
  Real_Estate_Space: "Real Estate / Space",
  Asset_Rental_Lease: "Asset Rental / Lease",
  Live_Event_Experience: "Live Event / Experience",
  Media_Stream_Broadcast: "Media Stream / Broadcast",
  Network_Community_Access: "Network / Community Access",
};

export const ROLE_LABELS: Record<RoleKey, string> = {
  founder_ceo: "Founder / CEO",
  co_founder: "Co-Founder",
  employee: "Employee",
  investor: "Investor",
  advisor: "Advisor",
  partner: "Partner",
  freelancer: "Freelancer",
  other: "Other",
};

// Q4 — Revenue stage options (single-select)
export const REVENUE_STAGE_OPTIONS: { key: Exclude<RevenueStage, "">; label: string; description: string }[] = [
  { key: "idea",          label: "Idea",          description: "Concept stage — nothing built yet" },
  { key: "mvp",           label: "MVP",           description: "Building a first prototype / MVP" },
  { key: "pre_revenue",   label: "Pre-revenue",   description: "Product exists — not charging yet" },
  { key: "early_revenue", label: "Early revenue", description: "First paying customers" },
  { key: "growth",        label: "Growth",        description: "Scaling revenue and team" },
  { key: "scale",         label: "Scale",         description: "Established, optimising" },
];

// Q5 — Priorities Raisy should focus on first (multi-select; plain language, never "bot").
// "other" reveals a free-text input (stored in firstTaskOther).
export const FIRST_TASK_OPTIONS: { key: FirstTask; label: string; description: string }[] = [
  { key: "raise_money",     label: "Raise money",          description: "Fundraising, investor outreach and materials" },
  { key: "lead_generation", label: "Lead generation",      description: "Finding and attracting new prospects" },
  { key: "admin",           label: "Admin & data entry",   description: "Repetitive paperwork and data capture" },
  { key: "follow_ups",      label: "Follow-ups & outreach", description: "Chasing leads, replies and check-ins" },
  { key: "reporting",       label: "Reporting",            description: "Pulling numbers into reports" },
  { key: "scheduling",      label: "Scheduling",           description: "Booking and coordinating time" },
  { key: "research",        label: "Research",             description: "Finding and summarising information" },
  { key: "other",           label: "Something else",       description: "Describe it in your own words" },
];

// D-061 journey gate — single source of truth for journeys enabled in v1. Ship narrow:
// equity-raise only. Enabling journey #2 = add its FirstTask key here (and ship its
// wizard/process); the wizard never shows disabled journeys to first-run users.
export const ENABLED_JOURNEYS: readonly FirstTask[] = ["raise_money"];

// Lean v1 own-path flow (D-061, ship narrow): 1 Type · 2 Company · 3 Role · 4 Stage ·
// 5 Review (Build on submit) · 6 Identity (only when a team self-match is found).
// Goals / Build scope / First task are DISABLED (defaulted: buildScope="standard",
// firstTasks=ENABLED_JOURNEYS) — their UI stays code-present in QuestionSteps + the
// wizard reducer, behind the journey gate, for journey #2. Step 1 (own vs. external)
// is a real, numbered, revisitable step.
export const STEP_TITLES: Record<number, string> = {
  1: "Whose entity is this?",
  2: "Add your company or product",
  3: "Your role",
  4: "What stage are you at?",
  5: "Review & confirm",
};

// External-entity path reuses steps 2-4 for a different flow (URL extract → relationship
// → review) — WizardShell's `title` override must use this instead of STEP_TITLES, or
// e.g. step 4 would show "What should Raisy help with?" while looking at the review screen.
// Step 1 (own vs. external choice) is shared with the "own" path's title.
export const EXTERNAL_STEP_TITLES: Record<number, string> = {
  1: "Whose entity is this?",
  2: "Add the entity",
  3: "How do you relate to this entity?",
  4: "Review & confirm",
};

// Lean v1 (D-061): 5 visible own-path steps; Review builds directly via its footer button.
// (A 6th rail label "Identity" is appended only when a team self-match is found.)
export const STEP_LABELS = ["Type", "Company", "Role", "Stage", "Review"];

export const WIZARD_TOTAL_STEPS = STEP_LABELS.length;

// External-entity path has 4 real, numbered wizard steps, all shown in the rail:
// 1=Type choice (own vs. external — revisitable) → 2=Entity/URL extract → 3=Relationship
// (StepRole with EXTERNAL_ROLE_OPTIONS) → 4=Review (reuses Step4Review). Skips Goals/Build
// scope/Stage/First task entirely — those are self-referential and don't apply to a third party.
export const EXTERNAL_STEP_LABELS = ["Type", "Entity", "Relationship", "Review"];
